import { useState, useEffect, useMemo } from 'react';
import Header from '../components/Header';
import FilterBar from '../components/FilterBar';
import QuestionPanel from '../components/QuestionPanel';
import CompareView from '../components/compare/CompareView';
import { getUserDashboards, getDashboardConfig, getCustomFilters } from '../utils/api';
import { COLORS } from '../utils/colors';

// Standard demographic filters — used as fallback only if API doesn't return them
const DEFAULT_FILTERS = {
  Party:  ['Democrat', 'Republican', 'Independent', 'Other'],
  Age:    ['18-29', '30-44', '45-59', '60+'],
  Gender: ['Male', 'Female', 'Non-binary'],
  Race:   ['White', 'Black', 'Hispanic', 'Asian', 'Other']
};

export default function DashboardPage() {
  const [dashboards, setDashboards] = useState([]);
  const [currentDashboard, setCurrentDashboard] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [demographicFilters, setDemographicFilters] = useState(DEFAULT_FILTERS);
  const [customFilters, setCustomFilters] = useState({});
  const [filterLabels, setFilterLabels] = useState({});
  const [filterValueMappings, setFilterValueMappings] = useState({});
  const [filterState, setFilterState] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('compare');   // compare view is the default tab

  // Load user's dashboards on mount
  useEffect(() => {
    async function init() {
      try {
        const dashes = await getUserDashboards();
        setDashboards(dashes);
        if (dashes.length > 0) {
          setCurrentDashboard(dashes[0]);
        }
      } catch (err) {
        setError('Failed to load dashboards: ' + err.message);
      }
    }
    init();
  }, []);

  // Load dashboard config when dashboard changes
  useEffect(() => {
    if (!currentDashboard) return;

    async function loadConfig() {
      setLoading(true);
      try {
        const [config, cf] = await Promise.all([
          getDashboardConfig(currentDashboard.id),
          getCustomFilters(currentDashboard.id)
        ]);

        setQuestions(config.questions || []);
        const df = config.demographicFilters;
        setDemographicFilters(df && Object.keys(df).length > 0 ? df : DEFAULT_FILTERS);
        setFilterLabels(config.filterLabels || {});
        setFilterValueMappings(config.filterValueMappings || {});
        setCustomFilters(cf || {});

        if (config.questions?.length > 0) {
          setSelectedQuestionId(config.questions[0].questionId);
        }

        // Reset filters when switching dashboards (only if filters are active,
        // to avoid creating a new {} reference that triggers unnecessary re-fetches)
        setFilterState(prev => Object.keys(prev).length > 0 ? {} : prev);
      } catch (err) {
        setError('Failed to load dashboard config: ' + err.message);
      } finally {
        setLoading(false);
      }
    }

    loadConfig();
  }, [currentDashboard?.id]);

  // Merge demographic + custom filters
  const allFilters = useMemo(() => {
    const merged = {};

    // Demographic filters from BigQuery (Party, Age, Gender, Race)
    for (const [label, values] of Object.entries(demographicFilters)) {
      merged[label] = values;
    }

    // Custom filters (e.g. Likely Voter, Education, Income)
    for (const [label, values] of Object.entries(customFilters)) {
      merged[label] = values.map(v => typeof v === 'string' ? v : v.value);
    }

    return merged;
  }, [demographicFilters, customFilters]);

  const selectedQuestion = questions.find(q => q.questionId === selectedQuestionId) || questions[0];

  const handleFilterChange = (label, values) => {
    setFilterState(prev => ({ ...prev, [label]: values }));
  };

  const handleClearAll = () => {
    setFilterState({});
  };

  const handleSwitchDashboard = (dash) => {
    setCurrentDashboard(dash);
  };

  if (error) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: COLORS.coral }}>
        <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>Error</div>
        <div style={{ fontSize: '14px', color: COLORS.textSecondary }}>{error}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Sticky top section */}
      <div style={stickyTopStyle}>
        <Header
          dashboards={dashboards}
          currentDashboard={currentDashboard}
          onSwitchDashboard={handleSwitchDashboard}
          lastUpdated={new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        />
        <div style={tabBarStyle}>
          <button
            type="button"
            aria-pressed={activeTab === 'compare'}
            onClick={() => setActiveTab('compare')}
            style={{ ...tabBtnStyle, ...(activeTab === 'compare' ? tabBtnActiveStyle : {}) }}
          >
            Compare View
          </button>
          <button
            type="button"
            aria-pressed={activeTab === 'classic'}
            onClick={() => setActiveTab('classic')}
            style={{ ...tabBtnStyle, ...(activeTab === 'classic' ? tabBtnActiveStyle : {}) }}
          >
            Standard View
          </button>
        </div>
        {activeTab === 'classic' && (
          <FilterBar
            filters={allFilters}
            filterLabels={filterLabels}
            filterState={filterState}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAll}
          />
        )}
      </div>

      {/* Content */}
      <div style={contentStyle}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px', color: COLORS.textMuted }}>
            Loading dashboard...
          </div>
        ) : activeTab === 'compare' ? (
          <>
            <CompareView
              clientKey={currentDashboard?.id}
              questions={questions}
              selectedQuestionId={selectedQuestionId}
              onSelectQuestion={setSelectedQuestionId}
              dimensions={allFilters}
              filterLabels={filterLabels}
              filterValueMappings={filterValueMappings}
            />
            <div style={footerStyle}>
              <span style={footerTextStyle}>
                Trendency Research &middot; Data refreshed nightly via BigQuery
              </span>
              <span style={footerTextStyle}>
                React Dashboard &middot; v1.0
              </span>
            </div>
          </>
        ) : (
          <>
            {/* Question selector (if multiple questions) */}
            {questions.length > 1 && (
              <div style={selectorPanelStyle}>
                <div>
                  <div style={selectorLabelStyle}>Question</div>
                  <select
                    style={selectorStyle}
                    value={selectedQuestionId || ''}
                    onChange={(e) => setSelectedQuestionId(e.target.value)}
                  >
                    {questions.map(q => (
                      <option key={q.questionId} value={q.questionId}>
                        {q.questionName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Question panel with charts */}
            {selectedQuestion && (
              <QuestionPanel
                question={selectedQuestion}
                clientKey={currentDashboard?.id}
                filters={filterState}
                filterValueMappings={filterValueMappings}
              />
            )}

            {/* Footer */}
            <div style={footerStyle}>
              <span style={footerTextStyle}>
                Trendency Research &middot; Data refreshed nightly via BigQuery
              </span>
              <span style={footerTextStyle}>
                React Dashboard &middot; v1.0
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Styles
const stickyTopStyle = { position: 'sticky', top: 0, zIndex: 50 };

const contentStyle = { padding: '20px 28px', maxWidth: '1400px', margin: '0 auto' };

const selectorPanelStyle = {
  background: '#fff', borderRadius: '14px', padding: '16px 24px',
  border: '1px solid #e2e6ea', marginBottom: '20px'
};

const selectorLabelStyle = {
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
  color: '#8a94a3', marginBottom: '6px'
};

const selectorStyle = {
  width: '100%', maxWidth: '500px', padding: '10px 14px',
  borderRadius: '10px', border: '1px solid #e2e6ea',
  background: '#f7f8fa', color: '#1a2332', fontSize: '14px',
  fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit'
};

const tabBarStyle = {
  background: '#fff', borderBottom: '1px solid #e2e6ea',
  padding: '8px 28px', display: 'flex', gap: '8px', justifyContent: 'center'
};

const tabBtnStyle = {
  border: '1px solid transparent', background: 'transparent',
  cursor: 'pointer', fontFamily: 'inherit', fontSize: '13.5px', fontWeight: 600,
  color: '#8a94a3', padding: '9px 22px', borderRadius: '10px'
};

const tabBtnActiveStyle = {
  color: '#fff', background: '#0f4c5c', borderColor: '#0f4c5c',
  fontWeight: 700, boxShadow: '0 1px 3px rgba(15,76,92,0.3)'
};

const footerStyle = {
  marginTop: '24px', padding: '16px 0', borderTop: '1px solid #e2e6ea',
  display: 'flex', justifyContent: 'space-between'
};

const footerTextStyle = { fontSize: '11px', color: '#8a94a3' };
